/**
 * Lumyn Live — participant join page.
 *
 * Phase 2c redesign (May 14, 2026):
 *  - Forced dark cinematic navy gradient + amber/orange accents — mirrors the
 *    host's Mission Control and feels like a continuation of the login.
 *  - Glassmorphic surfaces, big tap targets (≥ 56px), single column.
 *  - Per-question count-up ring matching the host's CastProgressBar style.
 *  - All data-testids, state flow, and PWA behavior preserved.
 *  - WS push + 15s poll fallback unchanged.
 */
import { useEffect, useState, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Flame, Loader2, Trophy, CheckCircle2, XCircle, Users, ShieldX,
} from 'lucide-react';
import api from '../api/client';
import { toast } from 'sonner';
import { useLiveSocket } from '../hooks/useLiveSocket';

// 15s poll fallback if WS is blocked (floor wifi captive portals, etc.)
const POLL_MS = 5000;
// Soft per-question budget for the participant ring — purely visual cue
const QUESTION_BUDGET_SECS = 30;

/* ── Haptic helpers ───────────────────────────────────────────────────────
 * navigator.vibrate is only present on Android Chrome / Firefox / Edge.
 * iOS Safari intentionally has no haptic API on the web — these calls
 * silently no-op there, which is the desired behavior. */
function buzz(pattern) {
  try {
    if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') {
      navigator.vibrate(pattern);
    }
  } catch {
    /* some browsers throw if vibrate is disabled at the system level */
  }
}
const HAPTIC_TAP = 25;                // tactile tick when an option is tapped
const HAPTIC_CORRECT = [30, 60, 30];  // double-tap "yes!" rhythm
const HAPTIC_WRONG = 180;             // single longer "ehh" buzz

/* ── Shared glass card surface ─────────────────────────────────────────── */
function GlassCard({ children, className = '', testId }) {
  return (
    <div
      data-testid={testId}
      className={`w-full max-w-md rounded-2xl border border-white/10 bg-white/[0.05] backdrop-blur-xl shadow-2xl shadow-black/30 p-6 ${className}`}
    >
      {children}
    </div>
  );
}

/* ── Count-up ring (mirrors host's CastProgressBar) ───────────────────── */
function CountUpRing({ startedAt, size = 44 }) {
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    if (!startedAt) { setElapsed(0); return undefined; }
    const start = new Date(startedAt).getTime();
    const tick = () => setElapsed(Math.max(0, (Date.now() - start) / 1000));
    tick();
    const id = setInterval(tick, 250);
    return () => clearInterval(id);
  }, [startedAt]);

  const STROKE = 4;
  const R = (size - STROKE) / 2;
  const C = 2 * Math.PI * R;
  const pct = Math.min(elapsed / QUESTION_BUDGET_SECS, 1);
  const offset = C * (1 - pct);
  const over = elapsed > QUESTION_BUDGET_SECS;

  return (
    <div className="relative" style={{ width: size, height: size }} data-testid="participant-ring">
      <svg width={size} height={size} className={over ? 'animate-pulse' : ''}>
        <circle cx={size/2} cy={size/2} r={R} stroke="rgba(255,255,255,0.10)" strokeWidth={STROKE} fill="none" />
        <circle
          cx={size/2} cy={size/2} r={R}
          stroke={over ? '#F43F5E' : 'var(--lumyn-live)'}
          strokeWidth={STROKE} strokeLinecap="round" fill="none"
          strokeDasharray={C} strokeDashoffset={offset}
          transform={`rotate(-90 ${size/2} ${size/2})`}
          style={{ transition: 'stroke-dashoffset 0.25s linear, stroke 0.25s' }}
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center font-mono font-bold tabular-nums text-white text-xs">
        {Math.floor(elapsed)}
      </div>
    </div>
  );
}

/* ── 1. CodeStep — the moment-of-truth single input ─────────────────────── */
function CodeStep({ initialCode = '', onLookup }) {
  const [code, setCode] = useState(initialCode);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e?.preventDefault();
    const trimmed = code.trim();
    if (trimmed.length < 4) { toast.error('Enter the 6-digit code'); return; }
    setBusy(true);
    try {
      const res = await api.get(`/live/lookup/${encodeURIComponent(trimmed)}`);
      onLookup({ refCode: trimmed, ...res.data });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Code not found — check and retry');
    } finally {
      setBusy(false);
    }
  };

  return (
    <GlassCard testId="code-step" className="space-y-5">
      <form onSubmit={submit} className="space-y-5" aria-label="Enter session code">
        <div className="space-y-3 text-center">
          <p className="text-[11px] uppercase tracking-[0.2em] text-slate-300/70">
            {'Session code'}
          </p>
          <Input
            id="ref-code"
            autoFocus
            inputMode="numeric"
            autoComplete="off"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            maxLength={6}
            placeholder="000000"
            aria-label="6-digit session code"
            className="h-20 text-5xl font-mono tracking-[0.35em] text-center bg-white/5 border-white/20 text-white placeholder:text-white/15 focus-visible:ring-[var(--lumyn-live)] focus-visible:ring-offset-0"
            data-testid="ref-code-input"
          />
        </div>
        <Button
          type="submit"
          disabled={busy || code.trim().length < 4}
          className="w-full h-14 text-base bg-[var(--lumyn-live)] hover:bg-[var(--lumyn-live)]/90 text-white shadow-lg shadow-[var(--lumyn-live)]/30 disabled:bg-white/10 disabled:text-white/30 disabled:shadow-none"
          data-testid="lookup-btn"
        >
          {busy ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : null}
          {'Continue'}
        </Button>
        <p className="text-xs text-slate-400 text-center">{'Ask your trainer for the 6-digit code.'}</p>
      </form>
    </GlassCard>
  );
}

/* ── 2. IdentifyStep — name + (optional, gated) email ──────────────────── */
function IdentifyStep({ session, onJoin, onBack }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const requireEmail = session.require_participant_email !== false;

  const submit = async (e) => {
    e?.preventDefault();
    if (!name.trim()) { toast.error('Enter your name'); return; }
    if (requireEmail && !email.trim()) { toast.error('Email is required for this session'); return; }
    setBusy(true);
    try {
      const res = await api.post('/live/join', {
        ref_code: session.refCode,
        display_name: name.trim(),
        email: email.trim() || undefined,
        device_id: getDeviceId(),
      });
      onJoin(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not join — check the code');
    } finally {
      setBusy(false);
    }
  };

  const fieldCls = 'h-12 text-base bg-white/5 border-white/15 text-white placeholder:text-white/30 focus-visible:ring-[var(--lumyn-live)] focus-visible:ring-offset-0';

  return (
    <GlassCard testId="identify-step" className="space-y-5">
      <form onSubmit={submit} className="space-y-5" aria-label="Identify yourself">
        <div className="text-center space-y-1.5">
          <p className="text-[11px] uppercase tracking-[0.2em] text-slate-300/70">Joining</p>
          <h2 className="text-xl font-display font-bold text-white" data-testid="identify-quiz-title">
            {session.quiz_title || 'Live session'}
          </h2>
        </div>

        <div className="space-y-2">
          <label htmlFor="name" className="block text-sm font-medium text-slate-200">{'Your name'}</label>
          <Input
            id="name" autoFocus autoComplete="name"
            value={name} onChange={(e) => setName(e.target.value)}
            placeholder="Sarah" aria-required="true"
            className={fieldCls} data-testid="name-input"
          />
        </div>
        <div className="space-y-2">
          <label htmlFor="email" className="block text-sm font-medium text-slate-200">
            {'Email'}
            {!requireEmail && <span className="text-slate-400"> {'(optional)'}</span>}
          </label>
          <Input
            id="email" type="email" autoComplete="email"
            value={email} onChange={(e) => setEmail(e.target.value)}
            placeholder="you@company.com" aria-required={requireEmail}
            className={fieldCls} data-testid="email-input"
          />
        </div>

        <div className="flex gap-2 pt-1">
          <Button
            type="button" variant="ghost" onClick={onBack}
            className="h-12 px-4 text-slate-300 hover:bg-white/10 hover:text-white"
            data-testid="identify-back-btn"
          >
            {'Back'}
          </Button>
          <Button
            type="submit" disabled={busy}
            className="flex-1 h-12 text-base bg-[var(--lumyn-live)] hover:bg-[var(--lumyn-live)]/90 text-white shadow-lg shadow-[var(--lumyn-live)]/30 disabled:bg-white/10 disabled:text-white/30 disabled:shadow-none"
            data-testid="join-btn"
          >
            {busy && <Loader2 className="w-5 h-5 mr-2 animate-spin" />}
            {'Join'}
          </Button>
        </div>
      </form>
    </GlassCard>
  );
}

/* ── 3. LobbyView — waiting state ───────────────────────────────────────── */
function LobbyView({ session }) {
  const nPeople = session.participant_count || 0;
  const peopleLabel = nPeople === 1 ? `${nPeople} person in` : `${nPeople} people in`;
  return (
    <GlassCard testId="lobby-view" className="text-center space-y-5">
      <div className="inline-flex items-center gap-2 bg-[var(--lumyn-live)]/15 border border-[var(--lumyn-live)]/30 rounded-full px-4 py-1.5 text-xs text-[var(--lumyn-live)]">
        <span className="relative flex h-2 w-2">
          <span className="absolute inline-flex h-full w-full rounded-full bg-[var(--lumyn-live)] opacity-75 animate-ping" />
          <span className="relative inline-flex rounded-full h-2 w-2 bg-[var(--lumyn-live)]" />
        </span>
        {'Waiting for trainer to start'}
      </div>
      <h1 className="text-2xl font-display font-bold text-white" data-testid="lobby-title">{session.quiz_title}</h1>
      <p className="text-sm text-slate-300 flex items-center justify-center gap-1">
        <Users className="w-4 h-4" />
        {peopleLabel}
      </p>
    </GlassCard>
  );
}

/* ── 4. QuestionView — large tap targets, single-column ─────────────────── */
function QuestionView({ session, participantId, onAnswered, lastResult }) {
  const q = session.current_question;
  const [picked, setPicked] = useState(null);
  const [busy, setBusy] = useState(false);
  const [streak, setStreak] = useState(null); // Drop 3: top-5 fastest-correct overlay

  useEffect(() => { setPicked(null); setStreak(null); }, [session.current_question_index]);

  if (!q) {
    return <p className="text-sm text-slate-400">Waiting for next question…</p>;
  }

  const submit = async (optionId) => {
    if (busy || session.already_answered) return;
    setBusy(true);
    setPicked(optionId);
    buzz(HAPTIC_TAP);
    try {
      const res = await api.post(`/live/sessions/${session.session_id}/answer`, {
        participant_id: participantId,
        // Phase 6 (May 2026 race fix): tell the server *which* question we
        // were looking at when we tapped. Server uses these to either
        // accept the tap under the 5 s late-grace window or reject with a
        // "re-tap" message — never silently mis-record against the next
        // question.
        question_index: session.current_question_index,
        question_id: session.current_question?.question_id,
        answer: optionId,
      }, {
        headers: { 'X-Participant-Id': participantId },
      });
      onAnswered(res.data);
      // Show "first 5 fastest correct" overlay; auto-dismiss after 2.5s
      if (res.data?.correct && res.data?.correct_rank && res.data.correct_rank <= 5) {
        setStreak(res.data.correct_rank);
        setTimeout(() => setStreak(null), 2500);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Submit failed');
      setPicked(null);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="w-full max-w-xl space-y-4" data-testid="question-view">
      {/* Q-of-M strip + ring */}
      <div className="flex items-center justify-between gap-3 px-1">
        <div className="text-[11px] uppercase tracking-[0.2em] text-slate-300/80">
          Question <span className="text-white font-semibold">{session.current_question_index + 1}</span>
          <span className="text-slate-500"> / {session.total_questions}</span>
        </div>
        <CountUpRing startedAt={session.question_started_at} />
      </div>

      <h2 className="text-xl sm:text-2xl font-display font-semibold leading-snug text-white">
        {q.question_text}
      </h2>

      <div className="grid grid-cols-1 gap-2.5">
        {(q.options || []).map((o, i) => {
          const showResult = lastResult && session.already_answered;
          const isPicked = picked === o.id;
          const isCorrect = showResult && lastResult.correct_options?.includes(o.id);
          const isWrongPick = showResult && isPicked && !isCorrect;
          let cls = 'border-white/15 bg-white/[0.05] text-white hover:bg-white/[0.10] active:scale-[0.99]';
          if (isCorrect) cls = 'border-[var(--lumyn-success)] bg-[var(--lumyn-success)]/15 text-white';
          else if (isWrongPick) cls = 'border-rose-500 bg-rose-500/15 text-white';
          else if (isPicked) cls = 'border-[var(--lumyn-live)] bg-[var(--lumyn-live)]/15 text-white';
          return (
            <button
              key={o.id || i}
              type="button"
              onClick={() => submit(o.id)}
              disabled={busy || session.already_answered}
              data-testid={`opt-${i}`}
              aria-label={`Option ${String.fromCharCode(65 + i)}: ${o.text}`}
              className={`min-h-[60px] w-full text-left px-4 py-3 rounded-xl border-2 text-base transition disabled:cursor-not-allowed disabled:opacity-90 ${cls}`}
            >
              <span className="font-mono text-sm mr-3 text-white/60">
                {String.fromCharCode(65 + i)}
              </span>
              <span className="align-middle">{o.text}</span>
              {isCorrect && <CheckCircle2 className="inline w-5 h-5 ml-2 text-[var(--lumyn-success)]" />}
              {isWrongPick && <XCircle className="inline w-5 h-5 ml-2 text-rose-400" />}
            </button>
          );
        })}
      </div>

      {session.already_answered && (
        <p
          className="text-sm text-slate-300 text-center py-3"
          aria-live="polite" data-testid="answer-locked-msg"
        >
          {'Answer locked. Waiting for trainer…'}
        </p>
      )}

      {streak !== null && <StreakOverlay rank={streak} />}
    </div>
  );
}

/* ── 4b. StreakOverlay — top-5 fastest-correct celebration ────────────── */
function StreakOverlay({ rank }) {
  const RANK_LABEL = ['1st!', '2nd', '3rd', '4th', '5th'];
  const label = RANK_LABEL[rank - 1] || `#${rank}`;
  const isFirst = rank === 1;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center pointer-events-none animate-fade-in-up"
      data-testid="streak-overlay"
      aria-live="polite"
    >
      <div
        className={`flex flex-col items-center gap-2 px-8 py-6 rounded-2xl border backdrop-blur-xl shadow-2xl ${
          isFirst
            ? 'border-amber-400/50 bg-amber-400/15 text-amber-100'
            : 'border-[var(--lumyn-live)]/40 bg-[var(--lumyn-live)]/15 text-white'
        }`}
        style={{ boxShadow: isFirst ? '0 0 60px rgba(251,191,36,0.4)' : '0 0 48px rgba(249,115,22,0.35)' }}
      >
        <Flame className={`w-12 h-12 ${isFirst ? 'text-amber-300' : 'text-[var(--lumyn-live)]'} animate-pulse-glow`} />
        <div className="text-center space-y-0.5">
          <p className="font-display font-bold text-3xl tracking-tight" data-testid="streak-rank">{label}</p>
          <p className="text-xs uppercase tracking-[0.18em] opacity-80">
            {isFirst ? 'Fastest correct!' : `Top ${rank} fastest`}
          </p>
        </div>
      </div>
    </div>
  );
}

/* ── 5. Leaderboard / Ended ─────────────────────────────────────────────── */
function Leaderboard({ board, highlightId }) {
  if (!board?.length) {
    return <p className="text-xs text-slate-400 italic">No scores yet</p>;
  }
  return (
    <ol className="space-y-1.5 text-sm text-left" data-testid="leaderboard">
      {board.slice(0, 5).map((p, i) => {
        const isMe = highlightId && p.participant_id === highlightId;
        return (
          <li
            key={p.participant_id}
            className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg transition ${
              isMe ? 'bg-[var(--lumyn-live)]/15 ring-1 ring-[var(--lumyn-live)]/40 text-white' : 'bg-white/[0.04] text-slate-200'
            }`}
          >
            <span className={`font-mono text-xs w-5 ${
              i === 0 ? 'text-amber-400 font-bold' :
              i === 1 ? 'text-slate-300 font-semibold' :
              i === 2 ? 'text-orange-300/80 font-semibold' : 'text-slate-500'
            }`}>{i + 1}</span>
            <span className="flex-1 truncate">
              {p.display_name}
              {isMe && <span className="ml-1.5 text-[10px] uppercase text-[var(--lumyn-live)]">you</span>}
            </span>
            <span className="font-semibold tabular-nums">{Math.round(p.score)}</span>
          </li>
        );
      })}
    </ol>
  );
}

function getDeviceId() {
  // Persistent per-browser fingerprint — one-join-per-device + host blocklist
  try {
    let id = localStorage.getItem('lumyn_device_id');
    if (!id) {
      id = (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`);
      localStorage.setItem('lumyn_device_id', id);
    }
    return id;
  } catch {
    return undefined;
  }
}

function RemovedView() {
  return (
    <div className="text-center space-y-3 max-w-sm" data-testid="removed-view">
      <ShieldX className="w-12 h-12 text-red-400 mx-auto" />
      <h2 className="text-xl font-bold text-white">Removed from session</h2>
      <p className="text-sm text-slate-400">
        The host removed you from this quiz. Your answers no longer count.
        If you think this was a mistake, speak to your trainer.
      </p>
    </div>
  );
}

function EndedView({ session, myScore, participantId }) {
  const wasRevoked = !!session?.revoked;
  return (
    <GlassCard testId="ended-view" className="text-center space-y-5">
      {wasRevoked ? (
        <>
          <div className="mx-auto w-20 h-20 rounded-full bg-rose-500/15 ring-1 ring-rose-400/30 flex items-center justify-center">
            <Flame className="w-10 h-10 text-rose-300" />
          </div>
          <div className="space-y-2" data-testid="revoked-banner">
            <h1 className="text-2xl font-display font-bold text-white">{'Session ended by host'}</h1>
            {session.revoked_reason && (
              <p className="text-sm text-slate-200 bg-rose-500/10 border border-rose-400/30 rounded-lg px-4 py-2 mx-auto max-w-xs">
                "{session.revoked_reason}"
              </p>
            )}
            <p className="text-xs text-slate-400">
              {'The trainer ended this session early. Your score so far is shown below.'}
            </p>
          </div>
        </>
      ) : (
        <div className="mx-auto w-20 h-20 rounded-full bg-amber-400/15 ring-1 ring-amber-400/30 flex items-center justify-center">
          <Trophy className="w-10 h-10 text-amber-300" />
        </div>
      )}
      <div className="space-y-1">
        {!wasRevoked && <h1 className="text-2xl font-display font-bold text-white">{'Session ended'}</h1>}
        <p className="text-sm text-slate-300">
          {'Your score'}:
          <span className="ml-1.5 font-display font-bold text-3xl text-[var(--lumyn-live)]">{Math.round(myScore)}</span>
          <span className="ml-1 text-xs text-slate-400">{'points'}</span>
        </p>
      </div>
      <div className="border-t border-white/10 pt-4">
        <p className="text-[11px] uppercase tracking-[0.18em] text-slate-400 mb-3">
          {'Final leaderboard'}
        </p>
        <Leaderboard board={session.leaderboard || []} highlightId={participantId} />
      </div>
    </GlassCard>
  );
}

/* ── 6. (Removed May 16, 2026) Participant-facing connection pip ──────────
 * The amber "RETRYING" badge was confusing non-technical BPO floor agents
 * even though the polling fallback kept the experience working. Connectivity
 * issues that actually break the experience surface via the lack of updates
 * — we'll add a friendly "Connection lost — refresh to rejoin" overlay if it
 * ever becomes a real problem. The trainer host page keeps a discreet dot
 * indicator so the trainer can still spot WS trouble at a glance.
 */

/* ── 7. Page shell ──────────────────────────────────────────────────────── */
export default function LiveJoinPage() {
  const { code } = useParams();

  const [lookup, setLookup] = useState(null);
  const [participantId, setParticipantId] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [session, setSession] = useState(null);
  const [lastResult, setLastResult] = useState(null);
  const lastQIndex = useRef(-2);

  // Auto-lookup when ?code= prefilled in URL
  useEffect(() => {
    if (!code || lookup) return;
    api.get(`/live/lookup/${encodeURIComponent(code)}`)
      .then((res) => setLookup({ refCode: code, ...res.data }))
      .catch(() => { /* user can retype */ });
  }, [code, lookup]);

  const pollState = useCallback(async () => {
    if (!sessionId || !participantId) return;
    try {
      const res = await api.get(`/live/sessions/${sessionId}/state`, {
        params: { participant_id: participantId },
        headers: { 'X-Participant-Id': participantId },
      });
      setSession(res.data);
      if (res.data.current_question_index !== lastQIndex.current) {
        setLastResult(null);
        lastQIndex.current = res.data.current_question_index;
      }
    } catch {
      /* network blip — retry next tick */
    }
  }, [sessionId, participantId]);

  useEffect(() => {
    if (!sessionId) return undefined;
    pollState();
    const id = setInterval(pollState, POLL_MS);
    return () => clearInterval(id);
  }, [sessionId, pollState]);

  // Reconnecting banner — shown when the WS has been silent for >3 s.
  // Helps participants understand "the room is alive, we're catching up"
  // vs "did my tap just vanish".
  const [wsHealthy, setWsHealthy] = useState(true);
  const wsDownSinceRef = useRef(null);

  useLiveSocket(sessionId, {
    participantId,
    onConnect: () => {
      wsDownSinceRef.current = null;
      setWsHealthy(true);
    },
    onDisconnect: () => {
      if (!wsDownSinceRef.current) wsDownSinceRef.current = Date.now();
      // Don't flip to false instantly — short blips are normal during
      // reconnect backoff. Only show the banner if it stays down >3 s.
      setTimeout(() => {
        if (wsDownSinceRef.current && Date.now() - wsDownSinceRef.current >= 3000) {
          setWsHealthy(false);
        }
      }, 3100);
    },
    onEvent: (msg) => {
      if (!msg) return;
      if (msg.event === 'hello') return;
      pollState();
    },
  });

  const onJoinSuccess = (res) => {
    setParticipantId(res.participant_id);
    setSessionId(res.session_id);
    try {
      // localStorage survives tab kill (Android Chrome reaping low-RAM tabs)
      // and accidental refresh — fixes the "refresh leads to starting from
      // the new page" trainer complaint. Scoped per session_id so leaving
      // one room and joining another doesn't conflate identities.
      localStorage.setItem(
        `live:${res.session_id}`,
        JSON.stringify({ participant_id: res.participant_id, joined_at: Date.now() }),
      );
    } catch {
      /* private mode / storage quota — fine, we still have in-memory state */
    }
  };

  useEffect(() => {
    if (participantId || !code) return;
    try {
      // Try localStorage first (post-May-2026), then sessionStorage as a
      // backward-compat fallback for browsers that joined before this
      // change rolled out.
      const stores = [localStorage, sessionStorage];
      for (const store of stores) {
        for (let i = 0; i < store.length; i++) {
          const key = store.key(i);
          if (!key?.startsWith('live:')) continue;
          const raw = store.getItem(key);
          const parsed = raw ? JSON.parse(raw) : null;
          if (parsed?.participant_id && Date.now() - parsed.joined_at < 6 * 3600 * 1000) {
            setSessionId(key.slice('live:'.length));
            setParticipantId(parsed.participant_id);
            return;
          }
        }
      }
    } catch {
      /* ignore */
    }
  }, [code, participantId]);

  const onAnswered = (res) => {
    // Tactile grading feedback — correct = double-tap "yes!", wrong = longer "ehh"
    buzz(res?.correct ? HAPTIC_CORRECT : HAPTIC_WRONG);
    setLastResult(res);
    pollState();
  };

  // May 2026: prefer the server-computed `my_score` (always accurate for
  // every participant, regardless of leaderboard cap). Fall back to the
  // truncated leaderboard for older builds.
  const meRow = (session?.leaderboard || []).find((p) => p.participant_id === participantId);
  const myScore = (typeof session?.my_score === 'number')
    ? session.my_score
    : (meRow?.score || 0);

  return (
    <div
      className="min-h-screen flex flex-col relative overflow-hidden"
      style={{
        minHeight: '100dvh',
        background: 'var(--color-panel)',
        color: 'var(--color-neutral-100)',
      }}
      data-testid="live-join-page"
    >
      {/* Quiet paper-dark panel — hairline texture only */}
      <div aria-hidden className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 opacity-[0.04]" style={{
          backgroundImage: 'radial-gradient(rgba(255,255,255,0.5) 1px, transparent 1px)',
          backgroundSize: '28px 28px',
        }} />
      </div>

      <div className="relative z-10 flex flex-col flex-1">
        {!participantId && (
          <div className="absolute top-3 left-3 z-50">
            
          </div>
        )}

        <header className="flex items-center justify-center gap-2.5 py-6">
          <span className="inline-flex items-center justify-center w-9 h-9 rounded-xl bg-[var(--lumyn-brand)]/15 ring-1 ring-[var(--lumyn-brand)]/30">
            <Flame className="w-5 h-5 text-[var(--lumyn-live)]" aria-hidden="true" />
          </span>
          <div className="flex flex-col leading-none">
            <span className="text-lg font-display font-bold tracking-tight">Lumyn Live</span>
            <span className="text-[10px] uppercase tracking-[0.2em] text-slate-400 mt-0.5">join in</span>
          </div>
        </header>

        {/* Reconnecting banner — only visible when WS has been down >3 s.
            Polling keeps state fresh in the background, but the user
            needs to understand "tap was received, just catching up". */}
        {participantId && !wsHealthy && (
          <div
            className="mx-auto -mt-2 mb-2 px-3 py-1.5 rounded-full text-xs bg-amber-500/15 text-amber-200 border border-amber-400/30 inline-flex items-center gap-2"
            role="status"
            data-testid="ws-reconnecting-banner"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />
            Reconnecting — your answers are still saving
          </div>
        )}

        <main className="flex-1 flex flex-col items-center justify-center px-5 pb-8">
          {!participantId && !lookup && (
            <CodeStep initialCode={code} onLookup={setLookup} />
          )}
          {!participantId && lookup && (
            <IdentifyStep
              session={lookup} onJoin={onJoinSuccess}
              onBack={() => setLookup(null)}
            />
          )}
          {participantId && !session && (
            <Loader2 className="w-7 h-7 animate-spin text-slate-400" />
          )}
          {participantId && session?.removed && <RemovedView />}
          {participantId && !session?.removed && session?.status === 'lobby' && <LobbyView session={session} />}
          {participantId && !session?.removed && session?.status === 'running' && (
            <QuestionView
              session={session} participantId={participantId}
              onAnswered={onAnswered} lastResult={lastResult}
            />
          )}
          {participantId && !session?.removed && session?.status === 'ended' && (
            <EndedView session={session} myScore={myScore} participantId={participantId} />
          )}
        </main>

        {!participantId && (
          <footer className="text-center text-[11px] text-slate-500 py-4">
            {'Powered by Lumyn · No account needed'}
          </footer>
        )}
      </div>
    </div>
  );
}
